'use client';

import * as React from 'react';
import {
  useController,
  UseControllerProps,
  FieldValues,
  FieldPath,
  Control,
  PathValue,
} from 'react-hook-form';

import { Slider, SliderProps } from '../slider';
import { Box } from '../box';
import { Text } from '../text';

export interface FormSliderProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> extends Omit<SliderProps, 'name' | 'value' | 'onChange' | 'ref' | 'defaultValue'> {
  /**
   * Field name for React Hook Form
   */
  name: TName;

  /**
   * Control from React Hook Form
   */
  control?: Control<TFieldValues>;

  /**
   * Validation rules for React Hook Form
   */
  rules?: UseControllerProps<TFieldValues, TName>['rules'];

  /**
   * Label for the slider
   */
  label?: string;

  /**
   * Whether to show the current value
   * @default true
   */
  showValue?: boolean;

  /**
   * Format function for displaying the value
   */
  formatValue?: (value: number) => string;

  /**
   * Error message
   */
  error?: string;

  /**
   * Helper text
   */
  hint?: string;

  /**
   * Whether the field is required
   */
  required?: boolean;
}

/**
 * Slider component integrated with React Hook Form
 * Automatically handles form registration and validation
 *
 * @example
 * ```tsx
 * <FormSlider
 *   name="volume"
 *   control={control}
 *   label="Volume"
 *   min={0}
 *   max={100}
 *   step={5}
 *   rules={{
 *     required: 'Please select a volume level',
 *     min: { value: 10, message: 'Volume must be at least 10' }
 *   }}
 *   formatValue={(val) => `${val}%`}
 * />
 * ```
 */
export function FormSlider<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  name,
  control,
  rules,
  label,
  showValue = true,
  formatValue = (val) => val.toString(),
  error,
  hint,
  required,
  ...props
}: FormSliderProps<TFieldValues, TName>) {
  const {
    field: { onChange, onBlur, value, ref },
    fieldState: { error: fieldError },
  } = useController({
    name,
    control,
    rules,
    defaultValue: (props.min || 0) as PathValue<TFieldValues, TName>,
  });

  const displayError = error || fieldError?.message;
  const currentValue = value || props.min || 0;

  return (
    <div ref={ref}>
      {/* Label and Value Display */}
      {(label || showValue) && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '0.5rem',
          }}
        >
          {label && (
            <label
              style={{
                fontSize: '0.875rem',
                fontWeight: 500,
                color: displayError ? '#EF4444' : '#374151',
              }}
            >
              {label}
              {required && ' *'}
            </label>
          )}
          {showValue && (
            <span
              style={{
                fontSize: '0.875rem',
                fontWeight: 500,
                color: '#6B7280',
              }}
            >
              {formatValue(currentValue)}
            </span>
          )}
        </div>
      )}

      {/* Slider */}
      <Slider
        {...props}
        value={currentValue}
        onChange={(val: number | number[]) => {
          onChange(val);
          onBlur();
        }}
        aria-label={label || props['aria-label']}
      />

      {/* Error Message */}
      {displayError && (
        <Box marginTop="0.25rem">
          <Text color="#EF4444" fontSize="0.75rem">
            {displayError}
          </Text>
        </Box>
      )}

      {/* Hint Message */}
      {hint && !displayError && (
        <Box marginTop="0.25rem">
          <Text fontSize="0.75rem" color="#6B7280">
            {hint}
          </Text>
        </Box>
      )}
    </div>
  );
}

FormSlider.displayName = 'FormSlider';
